export type BlogPostMeta = {
  slug: string;
  title: string;
  description: string;
  category: string;
  publishedAt: string;
  updatedAt: string;
  readTime: string;
  keywords: string[];
};

export const blogIndexMeta = {
  title: "Stock portfolio guides and historical replays — Equity Replay",
  description: "Clear, educational guides to stock portfolio allocation, historical performance, public filings, and what-if portfolio replays.",
};

export const blogPostMeta: BlogPostMeta[] = [
  {
    "slug": "13f-vs-13d-vs-form-4",
    "title": "13F vs. 13D vs. Form 4: What Each Filing Shows",
    "description": "Understand 13F vs 13D vs Form 4, what each public disclosure reports, how current it is, and how to read investor portfolio snapshots without overinterpreting them.",
    "category": "Public disclosures",
    "publishedAt": "2026-08-25",
    "updatedAt": "2026-08-25",
    "readTime": "7 min read",
    "keywords": [
      "13F vs 13D vs Form 4",
      "how to read 13F",
      "what is Form 4",
      "Schedule 13D explained",
      "public investor holdings",
      "institutional holdings disclosure"
    ]
  },
  {
    "slug": "401k-vs-ira",
    "title": "401(k) vs. IRA: What’s the Difference for Retirement Saving?",
    "description": "401(k) vs. IRA explained in plain English: compare employer matches, tax treatment, investment choices, fees, access, and how both accounts fit together.",
    "category": "Retirement planning",
    "publishedAt": "2026-09-21",
    "updatedAt": "2026-09-21",
    "readTime": "8 min read",
    "keywords": [
      "401(k) vs IRA",
      "401k vs IRA for beginners",
      "IRA vs 401(k)",
      "Roth IRA vs Roth 401(k)",
      "traditional 401(k) vs traditional IRA",
      "employer match",
      "retirement account comparison",
      "retirement savings basics"
    ]
  },
  {
    "slug": "alphabet-q3-2026-earnings",
    "title": "Alphabet Q3 2026 Earnings: Why EPS Fell While Revenue Grew",
    "description": "Alphabet Q3 2026 earnings explained: trace the gap between revenue growth and lower EPS, inspect investment losses, and frame a historical GOOGL comparison.",
    "category": "Earnings and market context",
    "publishedAt": "2026-10-31",
    "updatedAt": "2026-10-31",
    "readTime": "5 min read",
    "keywords": [
      "Alphabet Q3 2026 earnings",
      "Google Q3 2026 earnings",
      "why Alphabet EPS fell",
      "operating income vs net income",
      "Alphabet investment losses",
      "unrealized investment losses",
      "GOOGL historical stock comparison"
    ]
  },
  {
    "slug": "apy-vs-interest-rate",
    "title": "APY vs. Interest Rate: How to Read a Savings Account Offer",
    "description": "APY vs. interest rate explained with a $10,000 savings example. Learn how compounding changes the yield and how to avoid counting it twice in a calculator.",
    "category": "Saving and banking",
    "publishedAt": "2026-10-05",
    "updatedAt": "2026-10-05",
    "readTime": "5 min read",
    "keywords": [
      "APY vs interest rate",
      "annual percentage yield",
      "savings account interest calculation",
      "APY formula",
      "monthly compounding",
      "daily vs monthly compounding",
      "savings calculator annual rate"
    ]
  },
  {
    "slug": "backtesting-vs-historical-portfolio-replay",
    "title": "Backtesting vs. Historical Portfolio Replay: What’s the Difference?",
    "description": "Backtesting and historical portfolio replay are related but not identical. Learn which assumptions each approach can include and what Equity Replay actually measures.",
    "category": "How it works",
    "publishedAt": "2026-08-13",
    "updatedAt": "2026-08-13",
    "readTime": "4 min read",
    "keywords": [
      "portfolio backtest vs simulator",
      "historical portfolio simulator",
      "stock portfolio backtest"
    ]
  },
  {
    "slug": "brokerage-account-vs-portfolio-simulator",
    "title": "Brokerage Account vs. Portfolio Simulator: What’s the Difference?",
    "description": "Brokerage account vs portfolio simulator explained: learn what each one does, what a simulator can show, and why a historical replay is not a trade record.",
    "category": "Investing basics",
    "publishedAt": "2026-09-18",
    "updatedAt": "2026-09-18",
    "readTime": "5 min read",
    "keywords": [
      "brokerage account vs portfolio simulator",
      "what is a brokerage account",
      "portfolio simulator",
      "paper portfolio",
      "historical portfolio replay",
      "investment account basics"
    ]
  },
  {
    "slug": "cagr-calculator-annualized-return",
    "title": "CAGR Calculator: How to Calculate Annualized Investment Return",
    "description": "Use a CAGR calculator to turn a starting value, ending value, and time period into an annualized investment return, then read the result with the right caveats.",
    "category": "Investment returns",
    "publishedAt": "2026-09-09",
    "updatedAt": "2026-09-09",
    "readTime": "8 min read",
    "keywords": [
      "CAGR calculator",
      "compound annual growth rate calculator",
      "how to calculate CAGR",
      "CAGR formula",
      "annualized investment return",
      "CAGR vs average annual return",
      "CAGR vs IRR",
      "historical investment return"
    ]
  },
  {
    "slug": "costco-q4-2026-earnings",
    "title": "Costco Q4 2026 Earnings: Sales Growth, Comparable Sales, and EPS",
    "description": "Costco Q4 2026 earnings showed 11.2% net sales growth. Learn what adjusted comparable sales, the 16-week quarter, and a one-time EPS benefit add.",
    "category": "Earnings and market context",
    "publishedAt": "2026-09-26",
    "updatedAt": "2026-09-26",
    "readTime": "4 min read",
    "keywords": [
      "Costco Q4 2026 earnings",
      "Costco earnings September 2026",
      "Costco fiscal 2026 results",
      "Costco comparable sales Q4 2026",
      "Costco diluted EPS",
      "Costco net sales vs revenue",
      "how to read an earnings report",
      "Costco historical stock performance"
    ]
  },
  {
    "slug": "debt-snowball-vs-debt-avalanche",
    "title": "Debt Snowball vs Debt Avalanche: Compare Interest and Payoff Time",
    "description": "Debt snowball vs debt avalanche explained with the same $360 monthly budget. Compare interest, payoff time, and the assumptions behind a debt calculator.",
    "category": "Personal finance",
    "publishedAt": "2026-11-02",
    "updatedAt": "2026-11-02",
    "readTime": "6 min read",
    "keywords": [
      "debt snowball vs debt avalanche",
      "snowball vs avalanche calculator",
      "smallest balance or highest interest first",
      "debt payoff order",
      "debt avalanche interest savings",
      "debt snowball minimum payments"
    ]
  },
  {
    "slug": "emergency-fund-vs-investing",
    "title": "Emergency Fund vs. Investing: Where Should Your Next Dollar Go?",
    "description": "Emergency fund vs investing is less about picking a winner than matching money to its job. Use a practical framework for cash reserves, expensive debt, and long-term investing.",
    "category": "Personal finance",
    "publishedAt": "2026-09-14",
    "updatedAt": "2026-09-14",
    "readTime": "9 min read",
    "keywords": [
      "emergency fund vs investing",
      "should I save or invest",
      "how much emergency fund",
      "emergency savings",
      "cash reserve",
      "high-yield savings account",
      "saving before investing",
      "high-interest debt"
    ]
  },
  {
    "slug": "equal-weight-vs-market-cap-weighted-portfolios",
    "title": "Equal-Weight vs. Market-Cap-Weighted Portfolios",
    "description": "See how equal weighting and market-cap weighting give different influence to the same set of companies, then test the difference in a historical replay.",
    "category": "Portfolio construction",
    "publishedAt": "2026-08-13",
    "updatedAt": "2026-08-13",
    "readTime": "4 min read",
    "keywords": [
      "equal weight vs market cap weight",
      "market cap weighted portfolio",
      "equal weight portfolio"
    ]
  },
  {
    "slug": "etf-nav-vs-market-price",
    "title": "ETF NAV vs Market Price: Why the Returns Can Differ",
    "description": "ETF NAV vs market price explained with premium and discount calculations. See why NAV returns and closing-price returns can differ over the same dates.",
    "category": "ETFs and funds",
    "publishedAt": "2026-10-20",
    "updatedAt": "2026-10-20",
    "readTime": "7 min read",
    "keywords": [
      "ETF NAV vs market price",
      "ETF premium and discount",
      "NAV return vs market return",
      "ETF premium discount formula",
      "why ETF returns differ",
      "ETF closing price",
      "net asset value per share"
    ]
  },
  {
    "slug": "etf-vs-mutual-fund",
    "title": "ETF vs. Mutual Fund: What's the Difference for Beginners?",
    "description": "ETF vs. mutual fund explained for beginners: compare trading, pricing, fees, holdings, taxes, minimums, and the questions to ask before comparing funds.",
    "category": "ETFs and funds",
    "publishedAt": "2026-09-23",
    "updatedAt": "2026-09-23",
    "readTime": "10 min read",
    "keywords": [
      "ETF vs mutual fund",
      "ETF vs mutual fund for beginners",
      "mutual fund vs ETF",
      "exchange-traded fund vs mutual fund",
      "ETF fees",
      "mutual fund fees",
      "index fund vs ETF",
      "fund comparison"
    ]
  },
  {
    "slug": "high-yield-savings-account-vs-cd",
    "title": "High-Yield Savings Account vs. CD: Which Fits Your Savings Goal?",
    "description": "Compare a high-yield savings account vs. CD by access, rate changes, early-withdrawal rules, deposit insurance, and the savings goals each account may fit.",
    "category": "Saving and banking",
    "publishedAt": "2026-09-14",
    "updatedAt": "2026-10-05",
    "readTime": "9 min read",
    "keywords": [
      "high-yield savings account vs CD",
      "HYSA vs CD",
      "high-yield savings account",
      "certificate of deposit",
      "CD early withdrawal penalty",
      "emergency fund savings account",
      "CD ladder",
      "APY vs interest rate",
      "FDIC deposit insurance",
      "short-term savings"
    ]
  },
  {
    "slug": "how-are-stocks-taxed",
    "title": "How Are Stocks Taxed? Capital Gains, Dividends, and Cost Basis",
    "description": "How are stocks taxed? Learn the basic U.S. rules for capital gains, dividends, cost basis, and taxable brokerage accounts without confusing a historical return with a tax result.",
    "category": "Taxes and investing",
    "publishedAt": "2026-09-15",
    "updatedAt": "2026-09-15",
    "readTime": "10 min read",
    "keywords": [
      "how are stocks taxed",
      "stock investment taxes",
      "capital gains tax on stocks",
      "short-term capital gains",
      "long-term capital gains",
      "qualified dividends",
      "cost basis",
      "taxable brokerage account",
      "realized gains",
      "unrealized gains"
    ]
  },
  {
    "slug": "how-to-benchmark-a-stock-portfolio",
    "title": "How to Benchmark a Stock Portfolio: Choosing a Fair Comparison",
    "description": "Learn how to benchmark a stock portfolio, choose a relevant reference such as the S&P 500, and read a historical comparison without treating it as a prediction.",
    "category": "Reading the chart",
    "publishedAt": "2026-08-13",
    "updatedAt": "2026-08-13",
    "readTime": "8 min read",
    "keywords": [
      "how to benchmark a stock portfolio",
      "portfolio benchmark",
      "compare portfolio to S&P 500",
      "S&P 500 benchmark",
      "benchmark vs index",
      "historical portfolio performance"
    ]
  },
  {
    "slug": "how-to-build-a-stock-portfolio-with-percentages",
    "title": "How to Create a What-If Stock Portfolio With Percentages",
    "description": "A simple way to turn a list of stocks into an editable historical replay with clear weights and a chart you can inspect.",
    "category": "How to use Equity Replay",
    "publishedAt": "2026-08-10",
    "updatedAt": "2026-08-10",
    "readTime": "2 min read",
    "keywords": [
      "stock portfolio allocation",
      "portfolio percentages",
      "stock allocation simulator"
    ]
  },
  {
    "slug": "how-to-calculate-pmi",
    "title": "How to Calculate PMI: Monthly Cost Formula and Examples",
    "description": "Learn how to calculate PMI from your loan amount and annual rate, compare monthly cost examples, and check what a mortgage insurance calculator leaves out.",
    "category": "Financial calculators",
    "publishedAt": "2026-09-30",
    "updatedAt": "2026-09-30",
    "readTime": "5 min read",
    "keywords": [
      "how to calculate PMI",
      "PMI calculator",
      "monthly PMI cost",
      "private mortgage insurance calculation",
      "PMI formula",
      "loan-to-value ratio",
      "PMI cancellation"
    ]
  },
  {
    "slug": "how-to-calculate-portfolio-drawdown",
    "title": "How to Calculate Portfolio Drawdown: Formula and Example",
    "description": "Learn how to calculate portfolio drawdown from a previous peak, find maximum drawdown, and calculate the gain needed to recover after an investment loss.",
    "category": "Reading the chart",
    "publishedAt": "2026-09-24",
    "updatedAt": "2026-10-01",
    "readTime": "5 min read",
    "keywords": [
      "how to calculate portfolio drawdown",
      "maximum drawdown formula",
      "peak-to-trough loss",
      "investment loss recovery",
      "drawdown vs volatility"
    ]
  },
  {
    "slug": "how-to-calculate-portfolio-return-contribution",
    "title": "Portfolio Return Contribution: How to Calculate What Each Stock Added",
    "description": "Calculate portfolio return contribution from starting weights and returns. Follow a worked example in percentage points and dollars, then check a historical replay.",
    "category": "Reading the chart",
    "publishedAt": "2026-10-21",
    "updatedAt": "2026-10-21",
    "readTime": "7 min read",
    "keywords": [
      "portfolio return contribution",
      "contribution to portfolio return",
      "weighted portfolio return",
      "stock contribution to return",
      "percentage points vs percent",
      "beginning portfolio weights",
      "portfolio contribution vs attribution"
    ]
  },
  {
    "slug": "how-to-check-etf-overlap-with-your-stock-portfolio",
    "title": "How to Check ETF Overlap With Your Stock Portfolio",
    "description": "Learn how to check ETF overlap with a stock portfolio, calculate direct exposure from fund holdings, and compare the historical path without treating the result as advice.",
    "category": "Portfolio basics",
    "publishedAt": "2026-08-14",
    "updatedAt": "2026-08-14",
    "readTime": "7 min read",
    "keywords": [
      "how to check ETF overlap",
      "ETF overlap calculator",
      "ETF holdings overlap",
      "ETF and stock portfolio overlap",
      "portfolio overlap",
      "ETF diversification"
    ]
  },
  {
    "slug": "how-to-compare-portfolio-performance-over-time",
    "title": "How to Read a Portfolio Chart Across 1 Month, 6 Months, 5 Years, and 10 Years",
    "description": "The same allocation can tell different stories across different historical windows. Here is how to compare those views without overreading them.",
    "category": "Reading the chart",
    "publishedAt": "2026-08-10",
    "updatedAt": "2026-08-10",
    "readTime": "2 min read",
    "keywords": [
      "portfolio performance over time",
      "compare stock portfolios",
      "portfolio chart guide"
    ]
  },
  {
    "slug": "how-to-compare-two-stocks-over-time",
    "title": "How to Compare Two Stocks Over Time: A Fair Historical Framework",
    "description": "Learn how to compare two stocks over the same historical window, normalize their starting values, read price changes, and avoid treating a past chart as a forecast.",
    "category": "Stock comparisons",
    "publishedAt": "2026-08-16",
    "updatedAt": "2026-08-16",
    "readTime": "8 min read",
    "keywords": [
      "how to compare two stocks",
      "compare stock performance over time",
      "stock comparison calculator",
      "historical stock comparison",
      "compare two stocks side by side",
      "stock vs stock"
    ]
  },
  {
    "slug": "how-to-diversify-a-stock-portfolio-by-sector",
    "title": "How to Diversify a Stock Portfolio by Sector",
    "description": "Learn how sector diversification works, where stock overlap hides, and how to test an illustrative allocation across historical windows.",
    "category": "Portfolio basics",
    "publishedAt": "2026-08-13",
    "updatedAt": "2026-08-13",
    "readTime": "4 min read",
    "keywords": [
      "how to diversify a stock portfolio",
      "stock portfolio diversification",
      "sector allocation"
    ]
  },
  {
    "slug": "how-to-read-an-etf-factsheet",
    "title": "How to Read an ETF Factsheet: Fees, Holdings, and Risks",
    "description": "Learn how to read an ETF factsheet, check its objective, holdings, benchmark, expense ratio, and risks before comparing its historical path.",
    "category": "ETFs and funds",
    "publishedAt": "2026-09-18",
    "updatedAt": "2026-09-18",
    "readTime": "6 min read",
    "keywords": [
      "how to read an ETF factsheet",
      "ETF factsheet explained",
      "ETF expense ratio",
      "ETF holdings",
      "ETF benchmark",
      "ETF risks",
      "ETF research"
    ]
  },
  {
    "slug": "investment-fee-calculator",
    "title": "Investment Fee Calculator: How Fees Affect Your Returns Over Time",
    "description": "Use an investment fee calculator to estimate how annual fees affect an illustrative balance, contributions, and long-term growth before comparing costs.",
    "category": "Financial calculators",
    "publishedAt": "2026-09-16",
    "updatedAt": "2026-09-16",
    "readTime": "9 min read",
    "keywords": [
      "investment fee calculator",
      "investment calculator with fees",
      "investment fees calculator",
      "expense ratio calculator",
      "investment fee comparison",
      "cost of investment fees over time",
      "management fee calculator",
      "fee drag calculator"
    ]
  },
  {
    "slug": "is-savings-account-interest-taxable",
    "title": "Is Savings Account Interest Taxable? The $10 Rule and After-Tax Yield",
    "description": "Is savings account interest taxable? Learn when to report it, why the $10 Form 1099-INT threshold is not an exemption, and how to estimate after-tax yield.",
    "category": "Saving and banking",
    "publishedAt": "2026-10-26",
    "updatedAt": "2026-10-26",
    "readTime": "6 min read",
    "keywords": [
      "is savings account interest taxable",
      "high yield savings account taxes",
      "savings interest without 1099 INT",
      "savings interest under 10 dollars",
      "tax on interest not withdrawn",
      "after-tax savings yield"
    ]
  },
  {
    "slug": "is-stock-market-open-columbus-day-2026",
    "title": "Is the Stock Market Open on Columbus Day 2026?",
    "description": "Is the stock market open on Columbus Day 2026? Check October 12 stock and bond market schedules, Phoenix hours, and the dates behind a historical portfolio chart.",
    "category": "Market context",
    "publishedAt": "2026-10-10",
    "updatedAt": "2026-10-10",
    "readTime": "4 min read",
    "keywords": [
      "is the stock market open on Columbus Day 2026",
      "stock market Indigenous Peoples Day 2026",
      "October 12 stock market hours",
      "bond market Columbus Day",
      "NYSE holiday calendar",
      "Nasdaq holiday schedule",
      "historical closing prices",
      "portfolio data through date"
    ]
  },
  {
    "slug": "jpmorgan-q3-2026-earnings",
    "title": "JPMorgan Q3 2026 Earnings: Why Net Interest Income Grew 13% or 6%",
    "description": "JPMorgan Q3 2026 earnings explained: reconcile 13% net interest income growth with 6% excluding Markets, then frame a historical JPM stock comparison.",
    "category": "Earnings and market context",
    "publishedAt": "2026-10-17",
    "updatedAt": "2026-10-17",
    "readTime": "5 min read",
    "keywords": [
      "JPMorgan Q3 2026 earnings",
      "JPM earnings October 2026",
      "JPMorgan net interest income",
      "NII excluding Markets",
      "reported vs managed net interest income",
      "net interest income vs net interest margin",
      "JPM historical stock comparison"
    ]
  },
  {
    "slug": "money-market-account-vs-money-market-fund",
    "title": "Money Market Account vs Money Market Fund: Insurance and Access",
    "description": "Money market account vs money market fund: compare FDIC and SIPC protection, cash access, APY and seven-day yield, and the limits of a stock-only replay.",
    "category": "Saving and banking",
    "publishedAt": "2026-10-12",
    "updatedAt": "2026-10-12",
    "readTime": "7 min read",
    "keywords": [
      "money market account vs money market fund",
      "are money market funds FDIC insured",
      "FDIC vs SIPC",
      "money market deposit account",
      "brokerage cash sweep",
      "seven-day yield vs APY",
      "cash allocation",
      "emergency savings access"
    ]
  },
  {
    "slug": "mortgage-payment-calculator-taxes-insurance-pmi",
    "title": "Mortgage Payment Calculator With Taxes, Insurance, and PMI",
    "description": "Use a mortgage payment calculator with taxes, insurance, and PMI to see principal, interest, estimated housing costs, and the assumptions behind a monthly payment.",
    "category": "Financial calculators",
    "publishedAt": "2026-08-25",
    "updatedAt": "2026-08-25",
    "readTime": "7 min read",
    "keywords": [
      "mortgage payment calculator with taxes insurance PMI",
      "PITI mortgage calculator",
      "mortgage payment with PMI",
      "how to calculate a mortgage payment",
      "mortgage affordability calculator"
    ]
  },
  {
    "slug": "nike-q1-2027-earnings",
    "title": "Nike Q1 2027 Earnings: Why Margin Rose While Sales Fell",
    "description": "Nike Q1 2027 earnings showed lower sales but a higher gross margin. Understand the profit math, Nike Direct decline, and what the figures mean for comparisons.",
    "category": "Earnings and market context",
    "publishedAt": "2026-10-03",
    "updatedAt": "2026-10-03",
    "readTime": "4 min read",
    "keywords": [
      "Nike Q1 2027 earnings",
      "Nike earnings October 2026",
      "Nike gross margin",
      "gross margin vs gross profit",
      "Nike Direct vs wholesale",
      "currency-neutral revenue",
      "how to read an earnings report"
    ]
  },
  {
    "slug": "nominal-vs-real-return",
    "title": "Nominal vs Real Return: Did Your Portfolio Beat Inflation?",
    "description": "Nominal vs real return explained with worked examples: calculate inflation-adjusted investment returns, match CPI periods, and read a historical portfolio replay.",
    "category": "Investment returns",
    "publishedAt": "2026-10-06",
    "updatedAt": "2026-10-06",
    "readTime": "6 min read",
    "keywords": [
      "nominal vs real return",
      "inflation-adjusted investment return",
      "real rate of return formula",
      "did my portfolio beat inflation",
      "cumulative inflation",
      "real CAGR",
      "portfolio purchasing power"
    ]
  },
  {
    "slug": "pepsico-q3-2026-earnings",
    "title": "PepsiCo Q3 2026 Earnings: Reported vs Organic Revenue Growth",
    "description": "PepsiCo Q3 2026 earnings explained: separate reported and organic revenue growth, check core EPS, and connect the release to a historical stock comparison.",
    "category": "Earnings and market context",
    "publishedAt": "2026-10-10",
    "updatedAt": "2026-10-10",
    "readTime": "4 min read",
    "keywords": [
      "PepsiCo Q3 2026 earnings",
      "PepsiCo earnings October 2026",
      "PepsiCo organic revenue growth",
      "reported vs organic revenue",
      "PepsiCo core EPS",
      "earnings report growth measures",
      "PEP historical stock comparison"
    ]
  },
  {
    "slug": "portfolio-comparison-calculator",
    "title": "Portfolio Comparison Calculator: How to Compare Two Stock Portfolios Fairly",
    "description": "Use a portfolio comparison calculator to compare two stock portfolios by holdings, weights, overlap, historical paths, and an illustrative $10,000 starting amount.",
    "category": "Portfolio comparisons",
    "publishedAt": "2026-08-21",
    "updatedAt": "2026-08-21",
    "readTime": "8 min read",
    "keywords": [
      "portfolio comparison calculator",
      "compare two stock portfolios",
      "compare portfolio allocations",
      "stock portfolio comparison",
      "portfolio overlap",
      "historical portfolio performance",
      "hypothetical $10000 investment"
    ]
  },
  {
    "slug": "price-return-vs-total-return",
    "title": "Price Return vs Total Return: Does Your Stock Chart Include Dividends?",
    "description": "Price return vs total return explained with dividend examples. Calculate both, check reinvestment assumptions, and compare stock charts with fund performance reports.",
    "category": "Investment returns",
    "publishedAt": "2026-10-07",
    "updatedAt": "2026-10-07",
    "readTime": "7 min read",
    "keywords": [
      "price return vs total return",
      "does stock return include dividends",
      "total return formula",
      "dividend reinvestment return",
      "adjusted close vs close",
      "S&P 500 total return",
      "ETF performance comparison"
    ]
  },
  {
    "slug": "rolling-returns-vs-trailing-returns",
    "title": "Rolling Returns vs Trailing Returns: How Start Dates Change the Result",
    "description": "Rolling returns vs trailing returns explained with a three-year example. Calculate moving-window returns, check overlapping periods, and compare historical results.",
    "category": "Investment returns",
    "publishedAt": "2026-10-14",
    "updatedAt": "2026-10-14",
    "readTime": "7 min read",
    "keywords": [
      "rolling returns vs trailing returns",
      "how to calculate rolling returns",
      "three-year rolling returns",
      "rolling return vs CAGR",
      "overlapping return periods",
      "investment start date bias",
      "historical portfolio performance"
    ]
  },
  {
    "slug": "sharpe-ratio-vs-sortino-ratio",
    "title": "Sharpe Ratio vs Sortino Ratio: Comparing Portfolio Risk and Return",
    "description": "Sharpe ratio vs Sortino ratio explained with a worked example. Compare overall volatility with below-target shortfalls and check the inputs behind each number.",
    "category": "Reading the chart",
    "publishedAt": "2026-10-28",
    "updatedAt": "2026-10-28",
    "readTime": "7 min read",
    "keywords": [
      "Sharpe ratio vs Sortino ratio",
      "risk-adjusted portfolio return",
      "Sharpe ratio formula",
      "Sortino ratio formula",
      "downside deviation calculation",
      "minimum acceptable return",
      "negative Sharpe ratio"
    ]
  },
  {
    "slug": "sinking-fund-vs-emergency-fund",
    "title": "Sinking Fund vs Emergency Fund: How to Budget for Annual Bills",
    "description": "Sinking fund vs emergency fund explained with annual-bill examples, a monthly savings formula, and a way to avoid counting the same cash twice.",
    "category": "Personal finance",
    "publishedAt": "2026-10-19",
    "updatedAt": "2026-10-19",
    "readTime": "7 min read",
    "keywords": [
      "sinking fund vs emergency fund",
      "sinking fund examples",
      "how to calculate a sinking fund",
      "saving for annual bills",
      "irregular expenses budget",
      "monthly savings target",
      "savings goal calculator"
    ]
  },
  {
    "slug": "sp-500-vs-nasdaq-100",
    "title": "S&P 500 vs. Nasdaq-100: What Is the Difference?",
    "description": "Compare the S&P 500 and Nasdaq-100 by universe, weighting, overlap, and historical behavior without treating either index as a prediction.",
    "category": "Index comparisons",
    "publishedAt": "2026-08-13",
    "updatedAt": "2026-08-13",
    "readTime": "4 min read",
    "keywords": [
      "S&P 500 vs Nasdaq-100",
      "SPY vs QQQ",
      "S&P 500 and Nasdaq-100 difference"
    ]
  },
  {
    "slug": "stock-market-this-week-september-18-2026",
    "title": "Stock Market This Week: September 14–18, 2026 and the Fed Rate Hike",
    "description": "Review the stock market this week for September 14–18, 2026, including the Fed rate hike, 5% Treasury yield, oil prices, and split index performance.",
    "category": "Weekly market replay",
    "publishedAt": "2026-09-19",
    "updatedAt": "2026-09-19",
    "readTime": "7 min read",
    "keywords": [
      "stock market this week September 18 2026",
      "weekly stock market performance September 18 2026",
      "why did stocks fall this week",
      "September 2026 Fed rate hike",
      "S&P 500 weekly performance",
      "Nasdaq weekly performance",
      "Dow weekly performance",
      "Russell 2000 weekly performance",
      "10-year Treasury yield and stocks",
      "oil prices and stocks"
    ]
  },
  {
    "slug": "stock-portfolio-allocation-examples-by-style",
    "title": "Five Portfolio Ideas to Try in Equity Replay",
    "description": "Start with a concrete portfolio idea, assign percentages, and use the historical chart to understand how the mix would have moved.",
    "category": "Portfolio ideas",
    "publishedAt": "2026-08-10",
    "updatedAt": "2026-08-10",
    "readTime": "2 min read",
    "keywords": [
      "stock portfolio examples",
      "portfolio allocation examples",
      "sample stock portfolios"
    ]
  },
  {
    "slug": "stock-portfolio-tracker-vs-historical-replay",
    "title": "Stock Portfolio Tracker vs. Historical Replay: Which Tool Fits?",
    "description": "A stock portfolio tracker follows a real account, while a historical replay tests a defined allocation across past prices. Learn which question each tool can answer.",
    "category": "How it works",
    "publishedAt": "2026-08-26",
    "updatedAt": "2026-08-26",
    "readTime": "8 min read",
    "keywords": [
      "stock portfolio tracker",
      "portfolio performance tracker",
      "investment portfolio tracker",
      "historical portfolio performance",
      "stock portfolio simulator",
      "portfolio tracking app",
      "stock portfolio analysis"
    ]
  },
  {
    "slug": "stock-split-vs-reverse-stock-split",
    "title": "Stock Split vs Reverse Stock Split: Shares, Value, and Charts",
    "description": "Stock split vs reverse stock split explained with share-count examples, split-adjusted prices, cost basis, and checks before comparing historical returns.",
    "category": "Investing basics",
    "publishedAt": "2026-10-13",
    "updatedAt": "2026-10-13",
    "readTime": "6 min read",
    "keywords": [
      "stock split vs reverse stock split",
      "stock split calculator",
      "reverse stock split example",
      "split-adjusted stock price",
      "stock split cost basis",
      "why stock price changed after split",
      "historical stock comparison"
    ]
  },
  {
    "slug": "stock-vs-etf",
    "title": "Stock vs. ETF: What's the Difference for a Beginner?",
    "description": "Compare a stock vs. ETF by ownership, diversification, trading, fees, risk, and the questions to ask before using either in a portfolio.",
    "category": "Investing basics",
    "publishedAt": "2026-09-15",
    "updatedAt": "2026-09-15",
    "readTime": "10 min read",
    "keywords": [
      "stock vs ETF",
      "stock versus ETF",
      "what is an ETF",
      "individual stock vs ETF",
      "ETF diversification",
      "ETF holdings",
      "ETF expense ratio",
      "ETF vs mutual fund",
      "investing basics"
    ]
  },
  {
    "slug": "student-loan-payoff-calculator",
    "title": "Student Loan Payoff Calculator: How to Estimate Your Payoff Date",
    "description": "Use a student loan payoff calculator to estimate your payoff date and interest, compare fixed-payment scenarios, and see what an extra monthly amount changes.",
    "category": "Personal finance",
    "publishedAt": "2026-09-29",
    "updatedAt": "2026-09-29",
    "readTime": "7 min read",
    "keywords": [
      "student loan payoff calculator",
      "student loan payoff date",
      "how long to pay off student loans",
      "student loan interest calculator",
      "extra student loan payment",
      "student loan repayment calculator"
    ]
  },
  {
    "slug": "tesla-q3-2026-earnings",
    "title": "Tesla Q3 2026 Earnings: Why Free Cash Flow Rebounded but Fell 62%",
    "description": "Tesla Q3 2026 earnings explained: reconcile the free cash flow rebound with its 62% yearly decline, check capex, and frame a historical TSLA comparison.",
    "category": "Earnings and market context",
    "publishedAt": "2026-10-24",
    "updatedAt": "2026-10-24",
    "readTime": "5 min read",
    "keywords": [
      "Tesla Q3 2026 earnings",
      "Tesla free cash flow Q3 2026",
      "Tesla operating cash flow",
      "Tesla capital expenditures",
      "free cash flow vs net income",
      "quarter over quarter vs year over year",
      "TSLA historical stock comparison"
    ]
  },
  {
    "slug": "time-weighted-vs-money-weighted-return",
    "title": "Time-Weighted Return vs. Money-Weighted Return: Which Measure Fits?",
    "description": "Understand time-weighted return vs money-weighted return, how deposits and withdrawals change the result, and why a historical portfolio replay uses a narrower calculation.",
    "category": "Reading the chart",
    "publishedAt": "2026-08-20",
    "updatedAt": "2026-08-20",
    "readTime": "7 min read",
    "keywords": [
      "time-weighted return vs money-weighted return",
      "TWR vs MWR",
      "money-weighted return",
      "portfolio return with cash flows",
      "IRR vs TWR",
      "portfolio performance calculation"
    ]
  },
  {
    "slug": "tracking-error-vs-tracking-difference",
    "title": "Tracking Error vs Tracking Difference: How to Read an ETF's Index Gap",
    "description": "Tracking error vs tracking difference explained with ETF examples. Calculate the return gap, measure its variability, and check what a historical chart can show.",
    "category": "ETFs and funds",
    "publishedAt": "2026-10-27",
    "updatedAt": "2026-10-27",
    "readTime": "7 min read",
    "keywords": [
      "tracking error vs tracking difference",
      "ETF tracking difference",
      "tracking error formula",
      "ETF benchmark return",
      "tracking difference vs expense ratio",
      "low tracking error",
      "annualized tracking error"
    ]
  },
  {
    "slug": "vti-vs-voo-total-market-vs-sp-500",
    "title": "VTI vs. VOO: Total Market vs. S&P 500",
    "description": "Compare VTI vs VOO by market coverage, concentration, overlap, historical windows, and an illustrative starting amount without treating the result as a forecast.",
    "category": "ETF comparisons",
    "publishedAt": "2026-08-25",
    "updatedAt": "2026-08-25",
    "readTime": "7 min read",
    "keywords": [
      "VTI vs VOO",
      "VTI vs VOO performance",
      "VTI or VOO",
      "total market vs S&P 500",
      "VTI VOO overlap",
      "VTI vs VOO historical comparison"
    ]
  },
  {
    "slug": "weekly-stock-market-performance-august-28-2026",
    "title": "Weekly Stock Market Performance: What Moved in the Week Ending August 28, 2026",
    "description": "Review the weekly stock market performance for the week ending August 28, 2026, including the S&P 500, Dow, Nasdaq Composite, and Russell 2000.",
    "category": "Weekly market replay",
    "publishedAt": "2026-08-29",
    "updatedAt": "2026-08-29",
    "readTime": "6 min read",
    "keywords": [
      "weekly stock market performance",
      "stock market this week",
      "S&P 500 weekly performance",
      "Nasdaq weekly performance",
      "Russell 2000 weekly performance",
      "weekly market recap"
    ]
  },
  {
    "slug": "weekly-stock-market-performance-september-11-2026",
    "title": "Weekly Stock Market Performance: September 7 to 11, 2026",
    "description": "Review the weekly stock market performance for September 7 to 11, 2026, including the S&P 500, Dow, Nasdaq, Russell 2000, oil, inflation, and Treasury yields.",
    "category": "Weekly market replay",
    "publishedAt": "2026-09-12",
    "updatedAt": "2026-09-12",
    "readTime": "7 min read",
    "keywords": [
      "weekly stock market performance September 11 2026",
      "stock market this week",
      "S&P 500 weekly performance",
      "Nasdaq weekly performance",
      "Dow weekly performance",
      "Russell 2000 weekly performance",
      "why stocks fell this week",
      "oil prices and stocks",
      "CPI and stock market"
    ]
  },
  {
    "slug": "what-is-a-13f-filing",
    "title": "What Is a 13F Filing? How to Read an Investor Portfolio Disclosure",
    "description": "Understand what Form 13F reports, what it leaves out, why it is delayed, and how to turn a filing into a careful historical portfolio snapshot.",
    "category": "Public disclosures",
    "publishedAt": "2026-08-13",
    "updatedAt": "2026-08-13",
    "readTime": "4 min read",
    "keywords": [
      "what is a 13F filing",
      "how to read 13F",
      "institutional holdings",
      "investor portfolio disclosure"
    ]
  },
  {
    "slug": "what-is-a-stock-portfolio-simulator",
    "title": "How Equity Replay Calculates a Historical Portfolio Replay",
    "description": "See exactly how weights, close prices, date ranges, and a starting value become the chart—without interim trades or rebalancing.",
    "category": "How it works",
    "publishedAt": "2026-08-10",
    "updatedAt": "2026-08-10",
    "readTime": "2 min read",
    "keywords": [
      "historical portfolio replay",
      "portfolio simulator methodology",
      "weighted stock portfolio"
    ]
  },
  {
    "slug": "what-is-dollar-cost-averaging",
    "title": "What Is Dollar-Cost Averaging? A Plain-English Guide",
    "description": "What is dollar-cost averaging? Learn how regular investing works, how it differs from a lump-sum investment, and what a historical replay can and cannot show.",
    "category": "Investing basics",
    "publishedAt": "2026-09-18",
    "updatedAt": "2026-09-18",
    "readTime": "5 min read",
    "keywords": [
      "what is dollar-cost averaging",
      "dollar-cost averaging explained",
      "dollar-cost averaging vs lump sum",
      "regular investing",
      "market timing",
      "investing basics"
    ]
  },
  {
    "slug": "what-is-portfolio-rebalancing",
    "title": "What Is Portfolio Rebalancing? A Plain-English Guide",
    "description": "What is portfolio rebalancing? Learn how portfolio drift happens, how common methods work, and why costs and taxes matter.",
    "category": "Investing basics",
    "publishedAt": "2026-09-29",
    "updatedAt": "2026-09-29",
    "readTime": "6 min read",
    "keywords": [
      "what is portfolio rebalancing",
      "portfolio rebalancing explained",
      "how to rebalance a portfolio",
      "portfolio drift",
      "target asset allocation",
      "calendar rebalancing",
      "threshold rebalancing",
      "rebalancing taxes and fees"
    ]
  },
  {
    "slug": "what-the-buffett-portfolio-filing-really-shows",
    "title": "How to Read a Berkshire Hathaway Portfolio Snapshot",
    "description": "A Berkshire filing can be a useful historical snapshot, but it should not be presented as Warren Buffett’s complete personal portfolio.",
    "category": "Public disclosures",
    "publishedAt": "2026-08-10",
    "updatedAt": "2026-08-10",
    "readTime": "2 min read",
    "keywords": [
      "Buffett portfolio",
      "Berkshire Hathaway holdings",
      "Berkshire portfolio snapshot"
    ]
  },
  {
    "slug": "why-did-stocks-fall-after-jobs-report-august-2026",
    "title": "Why Did Stocks Fall After the Jobs Report? August 2026 Explained",
    "description": "Why did stocks fall after the jobs report? Review the September 4, 2026 market reaction, weekly index performance, and the rate expectations behind it.",
    "category": "Market context",
    "publishedAt": "2026-09-05",
    "updatedAt": "2026-09-05",
    "readTime": "5 min read",
    "keywords": [
      "why did stocks fall after jobs report",
      "August 2026 jobs report",
      "stock market weekly recap",
      "S&P 500 weekly performance",
      "Nasdaq weekly performance",
      "market reaction to payrolls",
      "September 2026 Fed meeting",
      "higher interest rates and stocks"
    ]
  }
];
